// src/BookingMoneyActions.js - the actions that move real money
import React, { useState } from 'react';
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress, Divider,
} from '@mui/material';
import { supabase } from './supabaseClient';
import { ink } from './designTokens';

const money = (v, c = 'USD') =>
  `${c === 'USD' ? '$' : ''}${parseFloat(v || 0).toFixed(2)}`;

/**
 * Refunding and releasing, behind a door that has to be opened on purpose.
 *
 * Both are irreversible from this console's side. A Stripe refund cannot be
 * un-refunded, and a cancelled authorization cannot be revived — funding a
 * client again means a fresh hold and their consent for it. So each one states
 * the amount and who it affects in a sentence, insists on a reason that is
 * written to the ledger, and asks for a word to be typed. The typing is not
 * theatre: it is the difference between meaning to click and having clicked.
 */
function ConfirmAction({
  open, onClose, title, description, confirmWord, amountLabel,
  defaultAmount, maxAmount, onConfirm,
}) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [amount, setAmount] = useState(defaultAmount ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const amountValid =
    amountLabel == null ||
    (parseFloat(amount) > 0 && (maxAmount == null || parseFloat(amount) <= maxAmount + 0.001));
  const ready = reason.trim().length > 2 && typed.trim().toUpperCase() === confirmWord && amountValid;

  const close = () => {
    setReason(''); setTyped(''); setError(null); setAmount(defaultAmount ?? '');
    onClose();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>{description}</Alert>

        {amountLabel && (
          <TextField
            fullWidth size="small" type="number" label={amountLabel}
            value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ mb: 2 }}
            helperText={maxAmount != null ? `At most ${money(maxAmount)}.` : undefined}
            error={!!amount && !amountValid}
          />
        )}

        <TextField
          fullWidth size="small" multiline minRows={2} label="Reason" sx={{ mb: 2 }}
          placeholder="Recorded against this booking and visible to other admins."
          value={reason} onChange={(e) => setReason(e.target.value)}
        />

        <TextField
          fullWidth size="small" label={`Type ${confirmWord} to confirm`}
          value={typed} onChange={(e) => setTyped(e.target.value)}
        />

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button
          variant="contained" color="error" disabled={!ready || busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
          onClick={async () => {
            setBusy(true); setError(null);
            try {
              await onConfirm({ reason: reason.trim(), amount: amount ? parseFloat(amount) : undefined });
              close();
            } catch (err) {
              setError(err.message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Working…' : title}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BookingMoneyActions({ booking, budget, onDone }) {
  const [openAction, setOpenAction] = useState(null);
  const [result, setResult] = useState(null);

  const call = async (fn, body) => {
    const { data: sess } = await supabase.auth.getSession();
    const { data, error } = await supabase.functions.invoke(fn, {
      body,
      headers: { Authorization: `Bearer ${sess?.session?.access_token}` },
    });
    // The edge functions answer with { error } on a refusal, and supabase-js
    // surfaces a non-2xx as `error` — either way the message is the useful part.
    if (error) throw new Error(data?.error || error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const paid = !!booking?.payment_completed;
  const authorized = parseFloat(budget?.authorized_amount || 0);
  const captured = parseFloat(budget?.captured_amount || 0);
  const canRelease =
    budget && captured === 0 && ['authorized', 'frozen'].includes(String(budget.status));
  // Only a live hold can be captured. The amount is not ours to choose — the
  // function takes the approved receipt total and refuses if there is none.
  const canCapture = budget && String(budget.status) === 'authorized';

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Money</Typography>
      <Typography variant="caption" sx={{ color: ink[500], display: 'block', mb: 1.5 }}>
        These move real money and cannot be undone from here.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small" variant="outlined" color="error"
          disabled={!paid}
          onClick={() => setOpenAction('refund')}
        >
          Refund the session
        </Button>
        <Button
          size="small" variant="outlined" color="error"
          disabled={!canRelease}
          onClick={() => setOpenAction('release')}
        >
          Release the shopping hold
        </Button>
        <Button
          size="small" variant="outlined" color="error"
          disabled={!canCapture}
          onClick={() => setOpenAction('capture')}
        >
          Capture without confirmation
        </Button>
      </Box>

      {!paid && (
        <Typography variant="caption" sx={{ color: ink[500], display: 'block', mt: 1 }}>
          Nothing was paid for this booking, so there is nothing to refund.
        </Typography>
      )}
      {budget && !canRelease && captured > 0 && (
        <Typography variant="caption" sx={{ color: ink[500], display: 'block', mt: 1 }}>
          {money(captured)} has already been captured from the allowance. Refund it rather than
          releasing the hold.
        </Typography>
      )}

      {result && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setResult(null)}>
          {result}
        </Alert>
      )}

      <Divider sx={{ mt: 2 }} />

      <ConfirmAction
        open={openAction === 'refund'}
        onClose={() => setOpenAction(null)}
        title="Refund"
        confirmWord="REFUND"
        amountLabel="Amount to refund"
        defaultAmount={booking?.price ?? ''}
        maxAmount={booking?.price ? parseFloat(booking.price) : undefined}
        description={`This returns money to the client through Stripe. It cannot be reversed — taking it back would mean charging them again, with their agreement. The booking's status is not changed by this.`}
        onConfirm={async ({ reason, amount }) => {
          await call('issue-refund', { booking_id: booking.id, amount, reason });
          setResult(`Refunded ${money(amount)}.`);
          onDone?.();
        }}
      />

      <ConfirmAction
        open={openAction === 'capture'}
        onClose={() => setOpenAction(null)}
        title="Capture"
        confirmWord="CAPTURE"
        description={`This charges the client for shopping they have not confirmed receiving. Only the approved receipt total is taken — you cannot set the amount, because what was spent is a fact rather than a judgement. Use it when the stylist has shopped, the receipts are filed, and the client has gone quiet; otherwise the hold lapses and the stylist is out of pocket for goods already bought. A client who disputes this can charge it back.`}
        onConfirm={async ({ reason }) => {
          const res = await call('admin-shopping-budget-action', {
            action: 'capture',
            budget_id: budget.id,
            reason,
          });
          setResult(
            res?.alreadyDone
              ? `Already ${res.status} — nothing further was taken.`
              : `Captured ${money(res?.captured_amount)}.`,
          );
          onDone?.();
        }}
      />

      <ConfirmAction
        open={openAction === 'release'}
        onClose={() => setOpenAction(null)}
        title="Release hold"
        confirmWord="RELEASE"
        description={`This cancels the ${money(authorized)} authorization on the client's card, handing the money back to them — usually visible within a few days. It cannot be revived: funding them again means a new hold and their consent for it. The stylist will no longer be able to spend against this budget.`}
        onConfirm={async ({ reason }) => {
          await call('admin-shopping-budget-action', {
            action: 'release',
            budget_id: budget.id,
            reason,
          });
          setResult(`Released the ${money(authorized)} hold.`);
          onDone?.();
        }}
      />
    </Box>
  );
}
